import React, { type Ref } from 'react';
import { SectionProps } from '@printdeal/ui-library';
import { Experiment } from '@printdeal/feature-management/react';
import { HomepageSectionProps, SectionWithExperimentProp } from '../types';
import { HomePageBannerData } from '../banner/types';
import { SECTION_COLLECTION } from '../../../templates/homepage/homepageConfiguration';
import { AudiencesData } from '../../../templates/homepage/homepage-helper';

interface SectionExperimentParent {
  variant?: SectionProps['variant'];
  allMarketingBanners?: HomePageBannerData[];
  scrollCuesInView?: boolean;
  onCueClick?: () => void;
  setScrollCuesInView?: (inView: boolean) => void;
  popularProductSectionNode?: Ref<HTMLDivElement> | null;
  audiencesDataFromSegment?: AudiencesData;
}

interface SectionRendererProps extends SectionExperimentParent {
  section: HomepageSectionProps;
}

interface SectionExperimentProps extends SectionExperimentParent {
  section: HomepageSectionProps | SectionWithExperimentProp;
}

const SectionRenderer = ({
  section,
  ...rest
}: SectionRendererProps) => {
  const sectionComponent = SECTION_COLLECTION[section.type];
  if (!sectionComponent) {
    return null;
  }

  const Component: React.ElementType = sectionComponent;

  return (
    <Component section={section} {...rest} />
  );
};

const SectionExperiment = ({
  section,
  ...rest
}: SectionExperimentProps) => {
  // Narrow down the type of section to either section or sectionExperiment
  // based on experimentId property.
  if (!('experimentId' in section)) {
    // Return Component if section is of type section.
    return (
      <SectionRenderer
        section={section as HomepageSectionProps}
        {...rest}
      />
    );
  }

  if (!section.control) {
    return null;
  }

  // Return Experiment if section is of type sectionExperiment.
  return (
    <Experiment id={section.experimentId as string}>
      <Experiment.Control>
        <SectionRenderer
          section={section.control as HomepageSectionProps}
          {...rest}
        />
      </Experiment.Control>
      <Experiment.Variant>
        <SectionRenderer
          section={section.variant as HomepageSectionProps}
          {...rest}
        />
      </Experiment.Variant>
    </Experiment>
  );
};

export default SectionExperiment;
